<template>
  <div class="eom-widget">
    <div v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate size="28" color="blue darken-1" />
    </div>
    <div v-else-if="fetchError" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
      <p class="text-caption grey--text mt-2">Unable to load data</p>
    </div>
    <div v-else class="kpi-card-body">
      <div class="d-flex align-center justify-space-between mb-2">
        <div>
          <div class="text-subtitle-2 font-weight-bold grey--text text--darken-2">
            EOM Readiness
          </div>
          <div class="text-caption grey--text">{{ monthLabel }}</div>
        </div>
        <div class="text-right">
          <div
            class="kpi-count font-weight-bold"
            :class="toneText(completedCount === totalCount ? 'green' : 'blue')"
          >
            {{ completedCount }}<span class="kpi-denom">/{{ totalCount }}</span>
          </div>
          <div class="text-caption grey--text">Complete</div>
        </div>
      </div>

      <v-progress-linear
        :value="totalCount > 0 ? (completedCount / totalCount) * 100 : 0"
        :color="completedCount === totalCount ? 'green darken-1' : 'blue darken-1'"
        height="8" rounded
        :background-color="isDark ? 'grey darken-3' : 'grey lighten-3'"
        class="mb-4"
      />

      <div
        v-for="step in steps"
        :key="step.id"
        class="step-row d-flex align-start mb-2"
      >
        <div class="step-icon-wrap mr-2 mt-1">
          <v-icon v-if="step.status === 'complete'" small :color="toneColor('green')">
            {{ icons.mdiCheckCircleOutline }}
          </v-icon>
          <v-icon v-else-if="step.status === 'pending'" small :color="toneColor('orange')">
            {{ icons.mdiClockOutline }}
          </v-icon>
          <v-icon v-else small color="grey lighten-1">
            {{ icons.mdiCircleOutline }}
          </v-icon>
        </div>

        <div class="flex-grow-1" style="min-width: 0;">
          <div class="text-caption font-weight-medium" :class="stepTextClass(step)">
            {{ step.label }}
          </div>
          <div class="text-caption grey--text" style="font-size: 10px;">
            {{ step.detail }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mdiCheckCircleOutline,
  mdiClockOutline,
  mdiCircleOutline,
} from '@mdi/js';
import { clients } from '../../../util/clients';
import WidgetThemeMixin from '../WidgetTheme.mixin';

const { backendRest } = clients.direct;

export default {
  name: 'EomReadinessWidget',
  mixins: [WidgetThemeMixin],
  data: () => ({
    loading: true,
    fetchError: false,
    completedCount: 0,
    totalCount: 6,
    steps: [],
    monthLabel: '',
    icons: {
      mdiCheckCircleOutline,
      mdiClockOutline,
      mdiCircleOutline,
    },
  }),
  async created() {
    await this.fetchData();
  },
  methods: {
    stepTextClass(step) {
      if (step.status === 'complete') return this.toneText('green');
      if (step.status === 'pending') return this.toneText('orange');
      return 'grey--text';
    },
    async fetchData() {
      this.loading = true;
      try {
        const now = new Date();
        const month = now.getMonth() + 1;
        const year = now.getFullYear();
        this.monthLabel = now.toLocaleDateString('en-US', {
          month: 'long', year: 'numeric',
        });
        const response = await backendRest.get('/dashboard/eom-status', {
          params: { month, year, fiscalYear: year },
        });
        const data = response.data || {};
        this.completedCount = data.completedCount || 0;
        this.totalCount = data.totalCount || 6;
        this.steps = data.steps || [];
      } catch (e) {
        console.error('EomReadinessWidget fetch failed', e);
        this.fetchError = true;
        this.completedCount = 0;
        this.totalCount = 6;
        this.steps = [];
      } finally {
        this.loading = false;
      }
    },
    async refresh() {
      await this.fetchData();
    },
  },
};
</script>

<style scoped>
.eom-widget { position: relative; }
.kpi-card-body { padding: 4px 0; }
.kpi-count {
  font-size: 2rem; font-weight: 800; line-height: 1; letter-spacing: -1px;
}
.kpi-denom {
  font-size: 1rem; font-weight: 400;
  color: var(--widget-muted-text, #9e9e9e); letter-spacing: 0;
}
.step-row {
  padding: 4px 0;
  border-bottom: 1px solid var(--widget-hairline, #f5f5f5);
}
.step-row:last-child { border-bottom: none; }
.step-icon-wrap { flex-shrink: 0; width: 20px; }
</style>
